import React, { useCallback, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import { ModelContext } from "contexts/ModelDetailContext";
import {
	getModelDetails,
	getModelLists,
} from "services/models/modelDetails/details";
import PageNotFound from "components/Elements/PageNotFound";
import { COLLAPSEDID } from "constants/modelDetails";
import { appPath, modelsPath } from "helpers/routePaths";

function ModelDetails(props) {
	const { t } = useTranslation();
	const [state, dispatch] = useContext(ModelContext);
	const [isLoading, setIsLoading] = useState(true);
	const [isError, setIsError] = useState(false);
	const [translationGate, setTranslationGate] = useState(null);
	const { siteAppID } =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));

	const navigate = useNavigate();
	const { id } = useParams();

	const fetchModelDetails = useCallback(async () => {
		const response = await Promise.all([
			getModelDetails(id),
			getModelLists(siteAppID),
		]);
		if (!response?.every((res) => res.status)) {
			setIsError(true);
		} else if (response[0].data?.translationStatus === "InProgress") {
			setTranslationGate({
				language: response[0].data?.translationTargetLanguage,
				country: response[0].data?.translationCountry,
			});
		} else {
			setTranslationGate(null);
			dispatch({
				type: "SET_MODEL_DETAIL",
				payload: {
					detail: response[0].data,
					activeModelVersion:
						response[1].data.find(
							(model) => model?.activeModelVersionID === +id
						)?.activeModelVersion ?? null,
				},
			});
		}
		setIsLoading(false);
	}, [id, dispatch, siteAppID]);
	useEffect(() => {
		fetchModelDetails();
		if (JSON.parse(localStorage.getItem("serviceLayoutData"))?.modelId !== id)
			localStorage.removeItem("serviceLayoutData");

		return () => sessionStorage.removeItem(COLLAPSEDID);
	}, [fetchModelDetails, id]);
	// console.log(isError);

	if (isLoading) {
		return <CircularProgress />;
	}

	if (isError) {
		return <PageNotFound message={t("message.modelNotFound")} />;
	}

	if (translationGate) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 2,
					p: 6,
					textAlign: "center",
				}}
			>
				<HourglassTopRoundedIcon
					sx={{ fontSize: 64, color: "#8a6d00" }}
				/>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					{t("title.translationInProgress")}
				</Typography>
				<Typography variant="body1" sx={{ maxWidth: 480 }}>
					{t("message.modelBeingTranslated", {
						language: `${translationGate.language}${
							translationGate.country
								? `-${translationGate.country.toUpperCase()}`
								: ""
						}`,
					})}
				</Typography>
				<Typography variant="body2" sx={{ color: "#666" }}>
					{t("message.refreshOrReturnToModels")}
				</Typography>
				<Box sx={{ display: "flex", gap: 2, mt: 1 }}>
					<Button
						variant="outlined"
						onClick={() => navigate(appPath + modelsPath)}
					>
						{t("action.backToModels")}
					</Button>
					<Button
						variant="contained"
						onClick={() => fetchModelDetails()}
					>
						{t("action.refresh")}
					</Button>
				</Box>
			</Box>
		);
	}

	return <>{props.children(state.modelDetail)}</>;
}

export default ModelDetails;
